"use client";

import { notifications } from "@mantine/notifications";
import { AuthorActions } from "Components/Admin/AdminAuthor/AuthorActions";
import { AuthorForm } from "Components/Admin/AdminAuthor/AuthorForm";
import { AuthorTable } from "Components/Admin/AdminAuthor/AuthorTable";
import { Header } from "Components/Admin/StyleComponent/Header";
import { useEffect, useState } from "react";
import {
  useAddAuthorMutation,
  useBulkDeleteAuthorsMutation,
  useDeleteAuthorMutation,
  useGetAllAuthorsQuery,
  useUpdateAuthorMutation,
} from "Services/Admin/AuthorApiSlice";
import { Author } from "Types/Admin/AdminAuthorType";

const PLANT = process.env.REACT_APP_PLANT;

const AdminAuthor = () => {
  const { data: apiAuthors = [], isLoading } = useGetAllAuthorsQuery({
    plant: PLANT || "",
  });

  const authors: Author[] = apiAuthors.map((author: Author) => ({
    id: author.id,
    author: author.author,
    isActive: author.isActive,
    crBy: author.crBy,
    upBy: author.upBy,
    createdAt: author.createdAt,
    updatedAt: author.updatedAt,
    plant: author.plant,
  }));

  const [addAuthor] = useAddAuthorMutation();
  const [updateAuthor] = useUpdateAuthorMutation();
  const [deleteAuthor, { isLoading: isDeletingAuthor }] =
  useDeleteAuthorMutation();
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<Author | null>(null);
  const [selectedAuthorId, setSelectedAuthorId] = useState<number | null>(null);

  const [bulkDeleteAuthors, { isLoading: isBulkDeletingAuthors }] =
  useBulkDeleteAuthorsMutation();
  const [selectedAuthors, setSelectedAuthors] = useState<number[]>([]);

  const [filteredAuthors, setFilteredAuthors] = useState<Author[]>([]);
  const [filterOption, setFilterOption] = useState("All");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    let filtered = authors ? [...authors] : [];

    if (filterOption === "Active") {
      filtered = filtered.filter((author) => author.isActive === "Y");
    } else if (filterOption === "Inactive") {
      filtered = filtered.filter((author) => author.isActive !== "Y");
    }

    if (searchText) {
      filtered = filtered.filter((author) =>
        author.author
          .trim()
          .toLowerCase()
          .includes(searchText.trim().toLowerCase()),
      );
    }

    const currentFilteredJSON = JSON.stringify(filtered);
    const previousFilteredJSON = JSON.stringify(filteredAuthors);

    if (currentFilteredJSON !== previousFilteredJSON) {
      setFilteredAuthors(filtered);
    }
  }, [authors, filterOption, searchText]);

  const handleViewAuthor = async (id: number) => {
    try {
      const authorToView = authors.find((author) => author.id === id);

      if (authorToView) {
        setEditingAuthor(authorToView);
        setViewMode(true);
        setShowForm(true);
      }
    } catch (error) {
      console.error("Failed to get author details:", error);
    }
  };

  const handleEditAuthor = async (id: number) => {
    try {
      const authorToEdit = authors.find((author) => author.id === id);

      if (authorToEdit) {
        setEditingAuthor(authorToEdit);
        setViewMode(false);
        setShowForm(true);
      }
    } catch (error) {
      console.error("Failed to get author details:", error);
    }
  };

  const handleSubmit = async (data: { author: string; isActive: string }) => {
    try {
      if (editingAuthor) {
        const response = await updateAuthor({
          id: editingAuthor.id,
          plant: PLANT || "",
          data: {
            author: data.author,
            isActive: data.isActive,
            upBy: "admin",
          },
        }).unwrap();

        notifications.show({
          message: response.message,
          color: "teal",
        });
      } else {
        const addResponse = await addAuthor({
          plant: PLANT || "",
          data: {
            author: data.author,
            isActive: data.isActive,
            crBy: "admin",
          },
        }).unwrap();

        notifications.show({
          message: addResponse.message,
          color: "teal",
        });
      }

      setShowForm(false);
      setEditingAuthor(null);
  } catch (error: any) {
    let errorMessage = "Failed to save author";
    if (error.data?.message) {
      errorMessage = error.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    notifications.show({
      message: errorMessage,
      color: "red",
    });
    console.error("Failed to save author:", error);
  }

  };

  const handleDelete = async (id?: number) => {
    const authorIdToDelete = id || selectedAuthorId;

    if (authorIdToDelete) {
      try {
        const res = await deleteAuthor({
          id: authorIdToDelete,
          plant: PLANT || "",
        }).unwrap();

        notifications.show({
          message: res.message,
          color: "teal",
        });

        if (authorIdToDelete === selectedAuthorId) {
          setSelectedAuthorId(null);
        }
      } catch (error: any) {
        let errorMessage = "Failed to delete author";
        if (error.data?.message) {
          errorMessage = error.data.message;
        } else if (typeof error.data === "string" && error.data) {
          errorMessage = error.data;
        } else if (error.message) {
          errorMessage = error.message;
        }
        notifications.show({
          message: errorMessage,
          color: "red",
        });
        console.error("Failed to delete author:", error);
      }
    }
  };

  const handleTableSelect = (ids: number[]) => {
    setSelectedAuthors(ids);
    setSelectedAuthorId(ids.length > 0 ? ids[0] : null);
  };

  const handleBulkDelete = async () => {
    if (selectedAuthors.length === 0) return;

    try {
      const count = selectedAuthors.length;
      const res = await bulkDeleteAuthors({
        ids: selectedAuthors,
        plant: PLANT || "",
      }).unwrap();
      notifications.show({
        message: res.message,
        color: "teal",
      });

      setSelectedAuthors([]);
      setSelectedAuthorId(null);
    } catch (error: any) {
      let errorMessage = "Failed to delete authors";
      if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (typeof error.data === "string" && error.data) {
        errorMessage = error.data;
      } else if (error.message) {
        errorMessage = error.message;
      }
      notifications.show({
        message: errorMessage,
        color: "red",
      });
      console.error("Failed to bulk delete authors:", error);
    }
  };

  const authorMappingFromDTOToRequest = (editingAuthor: Author | null) => {
    if (editingAuthor != null) {
      return {
        author: editingAuthor.author,
        isActive: editingAuthor.isActive,
      };
    } else {
      return null;
    }
  };
 if (isLoading && !showForm) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}
  return (
    <div className="font-body p-6 mx-auto">
      {!showForm ? (
        <>
          <Header
            title="Author"
            onAdd={() => setShowForm(true)}
            addLabel="Add Author"
          />
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden p-4 pt-8">
            <AuthorActions
              hideEdit={false}
              onDelete={handleBulkDelete}
              onFilterChange={setFilterOption}
              onSearchChange={setSearchText}
              isEditDisabled={!selectedAuthorId}
              isDeleteDisabled={!selectedAuthorId}
                isBulkDeleting={isBulkDeletingAuthors}
            />
            <AuthorTable
              authors={filteredAuthors}
              onSelectAuthor={handleTableSelect}
              onDeleteAuthor={(id) => handleDelete(id)}
              onViewAuthor={handleViewAuthor}
              onEditAuthor={handleEditAuthor}
              selectedAuthor={selectedAuthorId}
              
            
            />
          </div>
        </>
      ) : (
        <>
          <button
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
            onClick={() => {
              setShowForm(false);
              setEditingAuthor(null);
              setViewMode(false);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-arrow-left-icon lucide-arrow-left h-4 w-4"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            <span className="text-sm">Back</span>
          </button>
          <Header
            title={
              viewMode
                ? "View Author"
                : editingAuthor
                  ? "Edit Author"
                  : "Add Author"
            }
          />
          <AuthorForm
            initialValues={
              authorMappingFromDTOToRequest(editingAuthor) || {
                isActive: "Y",
                author: "",
              }
            }
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setViewMode(false);
              setEditingAuthor(null);
            }}
            mode={viewMode ? "view" : editingAuthor ? "edit" : "add"}
          />
        </>
      )}
    </div>
  );
};

export default AdminAuthor;
